import { AHText, AHTextFonts, Box, useScaleFactor } from "arkh";
import { DateTime } from "luxon";
import React, { useMemo } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import Color from "color";
import { graphql, useFragment } from "react-relay";

import { WidgetIpoolIntegrationEmployeeShifts_integrationIpoolShift$key } from "./__generated__/WidgetIpoolIntegrationEmployeeShifts_integrationIpoolShift.graphql";
import { WidgetIpoolIntegrationEmployeeShiftsPart_integrationIpoolShiftPart$key } from "./__generated__/WidgetIpoolIntegrationEmployeeShiftsPart_integrationIpoolShiftPart.graphql";
import { WidgetIpoolIntegrationEmployeeShifts_integrationIpoolEmployee$key } from "./__generated__/WidgetIpoolIntegrationEmployeeShifts_integrationIpoolEmployee.graphql";

export const EmployeeShifts = React.memo(EmployeeShiftsC);
function EmployeeShiftsC(props: {
  shifts: WidgetIpoolIntegrationEmployeeShifts_integrationIpoolEmployee$key;
  listHeight: number;
  boxWidth: number;
  isDoubleHeight: boolean;
  startD: DateTime;
  now: DateTime;

  mode: "TIME" | "TEAM";
}) {
  const employees = useFragment(
    graphql`
      fragment WidgetIpoolIntegrationEmployeeShifts_integrationIpoolEmployee on IntegrationIpoolEmployee
      @relay(plural: true) {
        id
        shifts {
          id
          ...WidgetIpoolIntegrationEmployeeShifts_integrationIpoolShift
        }
      }
    `,
    props.shifts
  );

  return (
    <View>
      {employees.map((e) => (
        <Box
          key={e.id}
          style={{ height: props.listHeight, justifyContent: "center" }}
        >
          {e.shifts.map((s) => (
            <Shift
              key={s.id}
              shift={s}
              boxWidth={props.boxWidth}
              isDoubleHeight={props.isDoubleHeight}
              now={props.now}
              showTime={props.mode === "TIME"}
              startD={props.startD}
            />
          ))}
        </Box>
      ))}
    </View>
  );
}
function Shift(props: {
  shift: WidgetIpoolIntegrationEmployeeShifts_integrationIpoolShift$key;
  isDoubleHeight: boolean;
  startD: DateTime;
  boxWidth: number;
  now: DateTime;
  showTime: boolean;
}) {
  const shift = useFragment(
    graphql`
      fragment WidgetIpoolIntegrationEmployeeShifts_integrationIpoolShift on IntegrationIpoolShift {
        id
        startAt
        endAt
        parts {
          id

          ...WidgetIpoolIntegrationEmployeeShiftsPart_integrationIpoolShiftPart
        }
      }
    `,
    props.shift
  );
  const shiftStartAt = useMemo(
    () => DateTime.fromISO(shift.startAt),
    [shift.startAt]
  );
  const startBox = useMemo(
    () => shiftStartAt.diff(props.startD).as("minutes"),
    [props.startD, shiftStartAt]
  );

  const width = useMemo(
    () => DateTime.fromISO(shift.endAt).diff(shiftStartAt).as("minutes"),
    [shift.endAt, shiftStartAt]
  );

  const sf = useScaleFactor();
  return (
    <Box
      key={shift.id}
      spaceLeft="xsmall"
      style={{
        overflow: "hidden",
        height: props.isDoubleHeight ? 30 * sf : 16 * sf,
        justifyContent: "center",
        left: (startBox / 30) * props.boxWidth,
        width: (width / 30) * props.boxWidth,
        position: "absolute",
      }}
    >
      {shift.parts.map((p) => (
        <ShiftPart
          key={p.id}
          part={p}
          boxWidth={props.boxWidth}
          isDoubleHeight={props.isDoubleHeight}
          now={props.now}
          showTime={props.showTime}
          shiftStartAt={shiftStartAt}
        />
      ))}
    </Box>
  );
}
function ShiftPart(props: {
  part: WidgetIpoolIntegrationEmployeeShiftsPart_integrationIpoolShiftPart$key;
  shiftStartAt: DateTime;
  now: DateTime;
  boxWidth: number;
  isDoubleHeight: boolean;
  showTime: boolean;
}) {
  const part = useFragment(
    graphql`
      fragment WidgetIpoolIntegrationEmployeeShiftsPart_integrationIpoolShiftPart on IntegrationIpoolShiftPart {
        startAt
        endAt
        type
        description
        color
      }
    `,
    props.part
  );

  const startAt = useMemo(() => DateTime.fromISO(part.startAt), [part.startAt]);
  const endAt = useMemo(() => DateTime.fromISO(part.endAt), [part.endAt]);

  const start = useMemo(
    () => startAt.diff(props.shiftStartAt).as("minutes"),
    [props.shiftStartAt, startAt]
  );

  const width = useMemo(
    () => endAt.diff(startAt).as("minutes"),
    [endAt, startAt]
  );

  const isActive = useMemo(() => endAt > props.now, [endAt, props.now]);
  const color = useMemo(
    () => Color(part.color ?? "#000").mix(Color("#FFF"), isActive ? 0 : 0.8),
    [isActive, part.color]
  );

  const style = useMemo<StyleProp<ViewStyle>>(
    () => ({
      height: "100%",
      backgroundColor: part.type === "BREAK" ? "transparent" : color.toString(),
      width: (width / 30) * props.boxWidth,
      left: Math.floor((start / 30) * props.boxWidth),
      top: 0,
      bottom: 0,
      position: "absolute",
      justifyContent: "center",
      flexDirection: "column",
    }),
    [color, part.type, props.boxWidth, start, width]
  );

  return (
    <Box cornerRadius="small" style={style}>
      {part.type === "BREAK" ? null : (
        <View style={{ opacity: isActive ? 1 : 0.5 }}>
          {props.isDoubleHeight ? (
            <>
              <ShiftPartContent
                color={color}
                showTime
                description={part.description}
                endAt={endAt}
                startAt={startAt}
              />
              <ShiftPartContent
                color={color}
                showTime={false}
                description={part.description}
                endAt={endAt}
                startAt={startAt}
              />
            </>
          ) : (
            <ShiftPartContent
              color={color}
              showTime={props.showTime}
              description={part.description}
              endAt={endAt}
              startAt={startAt}
            />
          )}
        </View>
      )}
    </Box>
  );
}
const ShiftPartContent = React.memo(ShiftPartContentC);
function ShiftPartContentC(props: {
  showTime: boolean;
  color: Color;
  startAt: DateTime;
  endAt: DateTime;
  description: string | null;
}) {
  const sf = useScaleFactor();

  return (
    <>
      <AHText
        type="detail"
        numberOfLines={1}
        ellipsizeMode="clip"
        style={{
          marginLeft: Math.round(3 * sf),
          fontSize: Math.round(10 * sf),
          display: props.showTime ? "flex" : "none",
        }}
        font={AHTextFonts.BOLDER}
        color={props.color.darken(0.1).isLight() ? "darkerText" : "lightText"}
      >
        {props.startAt.toFormat("HH:mm")}-{props.endAt.toFormat("HH:mm")}
      </AHText>
      <AHText
        type="detail"
        numberOfLines={1}
        style={{
          marginLeft: Math.round(3 * sf),
          fontSize: Math.round(12 * sf),
          display: props.showTime ? "none" : "flex",
        }}
        font={AHTextFonts.BOLD}
        color={props.color.darken(0.1).isLight() ? "darkerText" : "lightText"}
      >
        {props.description}
      </AHText>
    </>
  );
}
